import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

import { ErrorBoundary } from '@sentry/react';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useImmer } from 'use-immer';

import { ApiContext } from '_lib/api/apiContext';
import {
  PoolBrainCompany,
  PoolBrainCustomerLocationDTO,
  PoolBrainCustomerLocationListParams
} from '_lib/api/apiTypes.shim';

import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { Button } from '_lib/components/Button/Button';
import { Divider } from '_lib/components/Divider/Divider';
import { ErrorBoundaryFallback } from '_lib/components/ErrorBoundaryFallback/ErrorBoundaryFallback';

import { FlexBox } from '_lib/components/FlexBox/FlexBox';

import { MaskedPhoneDisplay } from '_lib/components/MaskedPhoneDisplay/MaskedPhoneDisplay';
import { Stack } from '_lib/components/Stack/Stack';

import { Typography } from '_lib/components/Typography/Typography';

import { CustomerSearch, CustomerSearchItem } from '_lib/featureComponents/Customer/CustomerSearch';
import { useAlertDialog } from '_lib/hooks/AlertDialogHook';

import { useAppDispatch, useAppSelector } from '_lib/redux/hooks';
import { PrimaryHeader, PrimaryHeaderIconButton } from '_lib/styles/PhoneCall.styled';
import { PhoneCallURLState } from '_lib/types/PhoneCall.types';

import { selectPhoneCallContext, setPartialPhoneCallContext } from '../../PoolBrainPhoneCall.slice';

import { CustomerLocationDetail } from './CustomerLocationDetail/CustomerLocationDetail';
import { CustomerLocationList } from './CustomerLocationList/CustomerLocationList';

type CustomerLocationPaneProps = {
  phoneCallURLState: PhoneCallURLState;
  company: PoolBrainCompany;
};

export const CustomerLocationPane = (props: CustomerLocationPaneProps) => {
  const { phoneCallURLState, company } = props;

  const scrollRef = useRef<HTMLDivElement>(undefined);

  const alertDialog = useAlertDialog();

  const api = useContext(ApiContext);
  const poolBrainApi = useMemo(() => api.poolBrain(company.id), [api, company.id]);

  const [customerLocationQueryParams, setCustomerLocationQueryParams] = useState<PoolBrainCustomerLocationListParams>({
    phoneNumber: phoneCallURLState.callerId
  });
  const customerLocationQuery = poolBrainApi.customerLocations.useUnpagedList({ params: customerLocationQueryParams });

  const { customerLocation } = useAppSelector(selectPhoneCallContext);
  const dispatch = useAppDispatch();

  const handleSetCustomerLocation = useCallback(
    async (customerLocation: PoolBrainCustomerLocationDTO | null) => {
      dispatch(
        setPartialPhoneCallContext({
          updates: { customerLocation }
        })
      );

      if (customerLocation && customerLocation.zipOrPostal === null) {
        await alertDialog.show({
          variant: 'warning',
          titleText: 'NO ZIP CODE FOR CUSTOMER',
          content: company.isZoned ? 'Please verify zip code and select zone' : 'Please verify zip code'
        });
      }
    },
    [alertDialog, company.isZoned, dispatch]
  );

  const firstFetch = useRef(true);
  useEffect(() => {
    if (firstFetch.current && customerLocationQuery.data) {
      firstFetch.current = false;
      if (customerLocationQuery.data.length === 1 && customerLocationQuery.data[0]) {
        handleSetCustomerLocation(customerLocationQuery.data[0]);
      }
    }
  }, [customerLocationQuery.data, handleSetCustomerLocation]);

  const [customerSearchItems, updateCustomerSearchItems] = useImmer<CustomerSearchItem[]>([
    {
      searchField: 'phoneNumber',
      value: customerLocationQueryParams.phoneNumber,
      isActive: true,
      isLastSearched: true
    },
    { searchField: 'name' },
    { searchField: 'address' }
  ]);

  return (
    <ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag('pane', 'Customer Location Pane');
      }}
      fallback={({ resetError }) => (
        <ErrorBoundaryFallback
          title='Error Displaying Customers'
          onRefresh={() => {
            resetError();
          }}
        />
      )}>
      <FlexBox
        sx={(theme) => ({
          height: '100%',
          width: '100%',
          flexDirection: 'column',
          backgroundColor: theme.palette.background.default
        })}>
        <PrimaryHeader
          sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%', height: 'fit-content' }}>
          <Stack useFlexGap sx={{ alignItems: 'flex-start', paddingX: 1.5, paddingY: 1, width: '100%' }}>
            <Typography variant='h3' text={customerLocation?.customer.name || 'Unknown'} />
            <MaskedPhoneDisplay variant='h3' preText='CID: ' text={phoneCallURLState.callerId ?? 'Invalid Caller ID'} />
          </Stack>
          {customerLocation && (
            <Stack direction='row'>
              <PrimaryHeaderIconButton onClick={() => handleSetCustomerLocation(null)} sx={{ marginRight: 2 }}>
                <CloseIcon />
              </PrimaryHeaderIconButton>
            </Stack>
          )}
        </PrimaryHeader>
        {customerLocation && (
          <CustomerLocationDetail customerLocation={customerLocation} showIcon sx={{ paddingX: 1.5 }} />
        )}
        {!customerLocation && (
          <>
            <CustomerSearch
              searchItems={customerSearchItems}
              onUpdateSearchItems={updateCustomerSearchItems}
              disabled={customerLocationQuery.isFetching}
              onCustomerSearch={(searchField) => {
                scrollRef.current?.scrollTo(0, 0);

                const searchState = customerSearchItems.find((item) => item.searchField === searchField);
                if (!searchState || searchState.isLastSearched) {
                  customerLocationQuery.refetch();
                  return;
                }

                if (searchState.searchField === 'phoneNumber') {
                  setCustomerLocationQueryParams({
                    phoneNumber: searchState.value
                  });
                } else if (searchState.searchField === 'name') {
                  setCustomerLocationQueryParams({
                    name: searchState.value
                  });
                } else if (searchState.searchField === 'address') {
                  setCustomerLocationQueryParams({
                    address: searchState.value
                  });
                }
              }}
            />
            <Divider />
            <FlexBox
              sx={(theme) => ({
                backgroundColor: theme.palette.background.default,
                position: 'relative',
                overflow: customerLocationQuery.isFetching ? 'none' : 'auto',
                flexGrow: 1
              })}
              ref={scrollRef}>
              <BackdropSpinner
                open={customerLocationQuery.isFetching}
                sx={{ alignItems: 'flex-start', paddingTop: 16 }}
              />
              {!customerLocationQuery.error && (
                <Stack useFlexGap spacing={0} flexGrow={1}>
                  {!!customerLocationQuery.data?.length && (
                    <CustomerLocationList
                      onSetCustomerLocation={handleSetCustomerLocation}
                      customerLocations={customerLocationQuery.data}
                    />
                  )}
                  {!customerLocationQuery.isFetching && !customerLocationQuery.data?.length && (
                    <Stack justifyContent='center' alignItems='center' padding={2}>
                      <Typography text='Customer Not Found' />
                      <Button
                        text='Find in Pool Brain'
                        variant='contained'
                        startIcon={<SearchIcon />}
                        onClick={() => {
                          window.open(company.dataProvider.providerOptions.customerSearchUrl, '_blank', 'popup');
                        }}
                      />
                    </Stack>
                  )}
                </Stack>
              )}
              {customerLocationQuery.error && (
                <ErrorBoundaryFallback
                  onRefresh={() => {
                    customerLocationQuery.refetch();
                  }}
                  title={'Error Searching Customers'}
                  sx={{ justifyContent: 'flex-start', paddingTop: 8 }}
                />
              )}
            </FlexBox>
          </>
        )}
      </FlexBox>
      {alertDialog.element}
    </ErrorBoundary>
  );
};
